import BaseNetwork, { Token } from "@/lib/classes/BaseNetwork";
import BTC from "@/lib/classes/BTC";
import BCH from "@/lib/classes/BCH";
import { NetworkKey } from "@/lib/constants/cryptos";
import {
  extractSavedKeys,
  StytchBackupData,
} from "@/lib/helpers/restore-wallets";
import { useStytchUser } from "@stytch/react";
import React, {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import EVM from "@/lib/classes/EVM";
import XRP from "@/lib/classes/XRP";
import DOGE from "@/lib/classes/DOGE";
import SOL from "@/lib/classes/SOL";
import { getTokens } from "@/lib/helpers/balances";
import { useWidget } from "@/lib/hooks/use-widget";
import XLM from "@/lib/classes/XLM";
import { useCallbacks } from "@/lib/hooks/use-callbacks";

type NetworksType = Record<NetworkKey, BaseNetwork>;

type TNetworksContext = {
  networks: NetworksType;
};

export const NetworkContext = createContext<TNetworksContext>({
  networks: {},
});

const NetworkProvider: FC<PropsWithChildren> = ({ children }) => {
  const networks = useRef<NetworksType>();
  const { user, fromCache } = useStytchUser();
  const [errors, setErrors] = useState<string[]>([]);
  const { onGetAddresses, onGetBalance } = useCallbacks();

  const { setIsLoading, setAddresses, setBalances, baseUrl } = useWidget();

  const updateTokens = useCallback(
    async (networkKey?: NetworkKey) => {
      try {
        const oldErrors = Array.from(errors);

        setIsLoading(true);
        setErrors([]);

        let newTokens: Record<NetworkKey, Token[] | null> = {};

        if (networkKey) {
          newTokens = await getTokens(baseUrl, [networkKey]);
        } else if (oldErrors.length > 0) {
          newTokens = await getTokens(baseUrl, oldErrors);
        } else {
          newTokens = await getTokens(baseUrl);
        }

        Object.entries(newTokens).forEach(([key, tokens]) => {
          if (!tokens) {
            setErrors(v => (v.includes(key) ? v : [...v, key]));
          } else {
            setBalances({ key, tokens });
          }
        });

        if (onGetBalance && newTokens) {
          onGetBalance(
            Object.entries(newTokens).reduce((prev, [key, tokens]) => {
              if (!tokens) return prev;
              return {
                ...prev,
                [key]: tokens,
              };
            }, {}),
          );
        }

        setIsLoading(false);
      } catch (error) {
        console.log("Error updating tokens", networkKey, errors, error);
      }
    },
    [errors],
  );

  useEffect(() => {
    if (fromCache || !user?.trusted_metadata) return;

    const wallets = extractSavedKeys(
      user.trusted_metadata.wallets as StytchBackupData,
    );

    if (!wallets || !wallets.ecdsa) {
      setIsLoading(false);
      return;
    }

    networks.current = {
      BTC: new BTC(wallets.ecdsa[0].xpub),
      BCH: new BCH(wallets.ecdsa[0].xpub),
      DOGE: new DOGE(wallets.ecdsa[0].xpub),
      XRP: new XRP(wallets.ecdsa[0].xpub),
      ETH: new EVM("ETH", wallets.ecdsa[0].xpub),
      BNB: new EVM("BNB", wallets.ecdsa[0].xpub),
      POL: new EVM("POL", wallets.ecdsa[0].xpub),
      AVAX: new EVM("AVAX", wallets.ecdsa[0].xpub),
      SOL: new SOL(wallets.ed25519[0].xpub),
      XLM: new XLM(wallets.ed25519[0].xpub),
    };

    setAddresses(
      Object.values(networks.current).reduce(
        (acc, value) => {
          return {
            ...acc,
            [value.network]: value.address,
          };
        },
        {} as Record<NetworkKey, string>,
      ),
    );

    if (onGetAddresses) {
      onGetAddresses(
        Object.values(networks.current).reduce(
          (acc, value) => {
            return {
              ...acc,
              [value.network]: value.address,
            };
          },
          {} as Record<NetworkKey, string>,
        ),
      );
    }

    updateTokens();
  }, [user?.trusted_metadata]);

  const value = useMemo(() => {
    return { networks: networks?.current || {} };
  }, [networks?.current]);

  return (
    <NetworkContext.Provider value={value}>{children}</NetworkContext.Provider>
  );
};

export default NetworkProvider;
